import ClaraCitation from '@/modules/ai/components/presentational/ClaraCitation.vue';
import { ClaraMessageObject } from '@/modules/ai/models/ClaraMessageObject';
import { EstateRagSimilarityDataMetadataObject } from '@/modules/ai/models/EstateRagSimilarityDataMetadataObject';
import { EstateRagDocumentObject } from '@/modules/ai/models/EstateRagDocumentObject';
import { EstateRagSimilarityDataObject } from '@/modules/ai/models/EstateRagSimilarityDataObject';
import { BaseRenderer } from '@/modules/core/ui/renderers/BaseRenderer';
import { VNode, h, Component } from 'vue';

interface NodeAttributes {
  [key: string]: string;
}

interface RenderResult {
  render: () => VNode;
}

interface CitationData {
  document: unknown;
  index: number;
  similarityData: EstateRagSimilarityDataObject;
}

interface ConsecutiveCitationResult {
  citations: Element[];
  nextIndex: number;
}

export class ClaraConversationViewPresenter extends BaseRenderer {
  private readonly CITATION_TAG: string = 'citation';
  private readonly HTML_PARSER: DOMParser = new DOMParser();
  private responseObject: ClaraMessageObject;
  private searchTerm: string;
  private cachedRenderResult: RenderResult | null = null;

  constructor(responseObject?: ClaraMessageObject, searchTerm?: string) {
    super();
    this.responseObject = responseObject as ClaraMessageObject;
    this.searchTerm = (searchTerm || '').trim();
  }

  get answerRender(): RenderResult {
    if (!this.cachedRenderResult) {
      const processedVNode: VNode = this.processDocument(this.responseObject);
      this.cachedRenderResult = {
        render: (): VNode => processedVNode
      };
    }
    return this.cachedRenderResult;
  }

  private processDocument(responseObject: ClaraMessageObject): VNode {
    const doc: Document = this.parseHtmlContent(responseObject.text as string);
    return this.processNode(doc.body, responseObject) as VNode;
  }

  private parseHtmlContent(htmlContent: string): Document {
    return this.HTML_PARSER.parseFromString(htmlContent, 'text/html');
  }

  private processNode(node: Node, response: ClaraMessageObject): VNode | string | null {
    if (this.isTextNode(node)) {
      const text: string = node.textContent || '';
      return this.shouldHighlight() ? this.createHighlightedText(text) : text;
    }

    if (this.isElementNode(node)) {
      const element: Element = node as Element;

      return this.isCitationElement(element)
        ? this.createCitationComponent(element, response)
        : this.createStandardElement(element, response);
    }

    return null;
  }

  private isTextNode(node: Node): boolean {
    return node.nodeType === Node.TEXT_NODE;
  }

  private isElementNode(node: Node): boolean {
    return node.nodeType === Node.ELEMENT_NODE;
  }

  private isCitationNode(node: Node): node is Element {
    return this.isElementNode(node) && this.isCitationElement(node as Element);
  }

  private isCitationElement(element: Element): boolean {
    return element.tagName.toLowerCase() === this.CITATION_TAG;
  }

  private renderCitationComponent(props: object, responseObject: ClaraMessageObject): VNode {
    return responseObject.isOutdated ? h('span') : h(ClaraCitation as Component, props);
  }

  private createCitationComponent(element: Element, responseObject: ClaraMessageObject): VNode {
    const citationData: CitationData | null = this.extractCitationData(element, responseObject);

    if (!citationData) {
      return h('span');
    }

    return this.renderCitationComponent(
      {
        documents: [citationData.document],
        indices: [citationData.index],
        similarityData: responseObject.similarityData
      },
      responseObject
    );
  }

  private createGroupedCitationComponent(
    citationElements: Element[],
    responseObject: ClaraMessageObject
  ): VNode {
    const citationData: CitationData[] = citationElements
      .map((element: Element) => this.extractCitationData(element, responseObject))
      .filter((data: CitationData | null): data is CitationData => data !== null);

    if (!citationData.length) {
      return h('span');
    }

    return this.renderCitationComponent(
      {
        documents: citationData.map((data: CitationData) => data.document),
        indices: citationData.map((data: CitationData) => data.index),
        similarityData: citationData.map((data: CitationData) => data.similarityData)
      },
      responseObject
    );
  }

  private extractCitationIndex(text: string): string {
    return text.replace(/[[\]]/g, '');
  }

  private createStandardElement(element: Element, responseObject: ClaraMessageObject): VNode {
    const tagName: string = element.tagName.toLowerCase();
    const attributes: NodeAttributes = this.getElementAttributes(element);
    const children: VNode[] = this.processChildNodes(element, responseObject) as VNode[];

    return h(tagName, attributes, children);
  }

  private getElementAttributes(element: Element): NodeAttributes {
    return Array.from(element.attributes).reduce<NodeAttributes>(
      (acc: NodeAttributes, attr: Attr) => {
        acc[attr.name] = attr.value;
        return acc;
      },
      {}
    );
  }

  private processChildNodes(
    element: Element,
    responseObject: ClaraMessageObject
  ): (VNode | string | null)[] {
    const childNodes: Node[] = Array.from(element.childNodes);
    const processedNodes: (VNode | string | null)[] = [];

    for (let i: number = 0; i < childNodes.length; i++) {
      const node: Node = childNodes[i];

      if (this.isCitationNode(node)) {
        const citationResult: ConsecutiveCitationResult = this.findConsecutiveCitations(
          childNodes,
          i
        );
        processedNodes.push(this.processCitations(citationResult.citations, responseObject));
        i = citationResult.nextIndex - 1;
      } else {
        processedNodes.push(this.processNode(node, responseObject));
      }
    }

    return processedNodes;
  }

  private isWhitespaceOnly(text: string): boolean {
    return /^\s*$/.test(text);
  }

  private findConsecutiveCitations(
    childNodes: Node[],
    startIndex: number
  ): ConsecutiveCitationResult {
    const citations: Element[] = [childNodes[startIndex] as Element];
    let currentIndex: number = startIndex + 1;

    while (currentIndex < childNodes.length) {
      const currentNode: Node = childNodes[currentIndex];

      if (this.isCitationNode(currentNode)) {
        citations.push(currentNode as Element);
        currentIndex += 1;
      } else if (
        this.isTextNode(currentNode) &&
        this.isWhitespaceOnly(currentNode.textContent || '')
      ) {
        currentIndex += 1;
      } else {
        break;
      }
    }

    return {
      citations,
      nextIndex: currentIndex
    };
  }

  private processCitations(citations: Element[], responseObject: ClaraMessageObject): VNode {
    return citations.length > 1
      ? this.createGroupedCitationComponent(citations, responseObject)
      : this.createCitationComponent(citations[0], responseObject);
  }

  private extractCitationData(
    element: Element,
    responseObject: ClaraMessageObject
  ): CitationData | null {
    const chunkId: string = this.extractCitationIndex(element.textContent ?? '');
    const index: number =
      responseObject.similarityData?.findIndex(
        (data: EstateRagSimilarityDataObject) => data.metadata?.chunkId === chunkId
      ) ?? 0;

    const similarityData: EstateRagSimilarityDataObject | undefined =
      responseObject.similarityData?.[index];

    if (!similarityData) {
      return null;
    }

    const document: EstateRagDocumentObject | undefined = responseObject.getDocument(
      similarityData.metadata as EstateRagSimilarityDataMetadataObject
    );

    if (!document) {
      return null;
    }

    return {
      document,
      index: index + 1,
      similarityData
    };
  }

  private shouldHighlight(): boolean {
    return this.searchTerm.length >= 2;
  }

  private createHighlightedText(text: string): VNode | string {
    if (!text || !this.shouldHighlight()) {
      return text;
    }

    const escapedSearch: string = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex: RegExp = new RegExp(`(${escapedSearch})`, 'gi');
    const parts: string[] = text.split(regex);

    if (parts.length === 1) {
      return text;
    }

    const children: VNode[] = parts
      .filter((part: string) => part.length > 0)
      .map((part: string) => {
        if (part.toLowerCase() === this.searchTerm.toLowerCase()) {
          return h(
            'span',
            {
              class:
                'search-highlight has-text-grey-darker px-1 is-radius-medium has-text-weight-semibold'
            },
            [part]
          );
        }
        return h('span', {}, [part]);
      });

    return h('span', {}, children);
  }
}
